/**
 * CompletedNoteCard — shared card component for completed notes.
 *
 * Used by both DashboardPage (RecentCompletedSection) and CompletedNotesPage.
 * Renders a single completed note row with 5 inline action buttons.
 */

import { useState } from 'react';
import type { NoteListItem } from './types';
import type { CopyState } from './useCopyToEmr';
import { useCopyToEmr } from './useCopyToEmr';
import { NotePreviewModal } from './NotePreviewModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return new Date(dateString).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatSessionDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString([], {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CompletedNoteCardProps {
  note: NoteListItem;
  onCopy: () => void;
  copyState: CopyState;
  onExportPdf: () => void;
  isExportingPdf: boolean;
  onSaveTxt: () => void;
  isSavingTxt: boolean;
  onEdit: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CompletedNoteCard({
  note,
  onCopy,
  copyState,
  onExportPdf,
  isExportingPdf,
  onSaveTxt,
  isSavingTxt,
  onEdit,
}: CompletedNoteCardProps) {
  const age = formatAge(note.completedAt ?? note.updatedAt);
  const sessionDate = formatSessionDate(note.sessionDate);

  // Preview modal state — the card owns its own copy state for inside the modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const { state: modalCopyState, copy: modalCopyToEmr } = useCopyToEmr();

  return (
    <>
      <div className="clinical-card p-4 flex items-start gap-3">
        {/* Green accent dot */}
        <div className="flex-shrink-0 mt-1.5" aria-hidden="true">
          <div className="w-2.5 h-2.5 rounded-full bg-safety-green" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-heading text-sm font-semibold text-clinical-charcoal truncate">
            {note.templateName}
          </p>
          <p className="text-xs font-body text-clinical-text-muted mt-0.5 truncate">
            {sessionDate}
          </p>
          {note.patientRef && (
            <p className="text-xs font-body text-clinical-text-muted truncate mt-0.5">
              Patient: {note.patientRef}
            </p>
          )}
          <p className="text-xs font-body text-clinical-text-muted mt-1">{age}</p>
        </div>

        {/* Actions — flex-wrap row; wraps naturally on narrow screens */}
        <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-2">
          {/* Preview — FIRST */}
          <button
            type="button"
            className="btn-ghost px-3 py-1.5 text-xs min-h-[40px] flex items-center gap-1"
            onClick={() => setPreviewOpen(true)}
            aria-label={`Preview ${note.templateName} note`}
          >
            <EyeIcon />
            Preview
          </button>

          {/* Copy to EMR */}
          <button
            type="button"
            className="btn-primary px-3 py-1.5 text-xs min-h-[40px]"
            onClick={onCopy}
            disabled={copyState === 'copying'}
            aria-label={`Copy ${note.templateName} to EMR`}
          >
            {copyState === 'copying'
              ? 'Copying...'
              : copyState === 'success'
              ? 'Copied'
              : copyState === 'error'
              ? 'Copy failed'
              : 'Copy to EMR'}
          </button>

          {/* PDF */}
          <button
            type="button"
            className="btn-amber px-3 py-1.5 text-xs min-h-[40px] flex items-center gap-1.5"
            onClick={onExportPdf}
            disabled={isExportingPdf}
            aria-label={`Export ${note.templateName} as PDF`}
          >
            {isExportingPdf ? (
              <>
                <SpinnerIcon />
                Exporting...
              </>
            ) : (
              'PDF'
            )}
          </button>

          {/* .txt */}
          <button
            type="button"
            className="btn-secondary px-3 py-1.5 text-xs min-h-[40px]"
            onClick={onSaveTxt}
            disabled={isSavingTxt}
            aria-label={`Save ${note.templateName} as text file`}
          >
            {isSavingTxt ? 'Saving...' : '.txt'}
          </button>

          {/* Edit */}
          <button
            type="button"
            className="btn-ghost px-3 py-1.5 text-xs min-h-[40px] flex items-center gap-1"
            onClick={onEdit}
            aria-label={`Edit ${note.templateName} note`}
          >
            <PencilIcon />
            Edit
          </button>
        </div>
      </div>

      {/* Preview modal — rendered in a portal-like fashion via conditional mount */}
      {previewOpen && (
        <NotePreviewModal
          noteId={note.id}
          noteTitle={note.templateName}
          onClose={() => setPreviewOpen(false)}
          onCopyToEmr={() => void modalCopyToEmr(note.id)}
          copyState={modalCopyState}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline icons
// ---------------------------------------------------------------------------

function SpinnerIcon() {
  return (
    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
